import React, { useRef, useState } from 'react';
import { evidenceFilesApi } from '../../utils/api';
import { filenameMismatchNotice, namedSourcesFor, ACCEPT, uploadErrorMessage } from './EvidenceSurface';
import { receiptFor } from './UploadReceipt';

// WU-C (staging fix-it sprint 2026-08, RJ ratified option C1 on 2026-08-07):
// the Outcome's call to action, rendered where its state is reported.
//
// The Delivery status card used to report "Awaiting evidence" and then point
// at another tab — and its primary button offered a verb the state could not
// honour ("Submit evidence for checking" with zero files spends a run that
// short-circuits every criterion to needs_review). This is the right verb for
// that state, one click from done: the target NAMES the artifact the agreed
// spec asked for (from the Outcome's required_artifacts) and uploading it
// starts checking on its own — no tab hop, no second decision.
//
// Agency-only by construction: the client never sees the agency's CTA
// (fix plan WU-C, client-view mirror). Callers gate on role.

// RJ decision 2026-08-09 (WU-C QA B1): the CTA names the FILE, never a
// phrase parsed out of the spec's prose. required_artifacts is free text
// authored by the seller, and every attempt to lift a noun phrase out of it
// produced word-salad on ordinary sentences — "Client's CRM export
// (crm-lead-export.csv)" titled the page's primary button "Add the s CRM
// export". A file name is exact, is what the user drags out of Finder, and
// cannot be nonsense. This deviates from the ratified C1 mockup's prose
// title by RJ's explicit call; the mockup's structure (name the artifact,
// one click, no tab hop) is unchanged.

// The single file this Outcome's spec names, when it names exactly one.
export function namedFileFor(outcome) {
  const names = namedSourcesFor(outcome);
  return names.length === 1 ? names[0] : null;
}

export function dropTitleFor(outcome) {
  const names = namedSourcesFor(outcome);
  if (names.length === 1) return `Add ${names[0]}`;
  if (names.length === 2) return `Add ${names[0]} or ${names[1]}`;
  // Three or more would be a list nobody reads; the spec text above the
  // target already carries them.
  return 'Add the evidence for this outcome';
}

export function dropSubtitleFor(outcome) {
  // The title already carries the file name when there is one, so the
  // sub-line never repeats it.
  const named = namedSourcesFor(outcome).length > 0;
  return `Drop ${named ? 'it' : 'the file'} here, or click to choose it. Checking starts on its own once it lands.`;
}

// onNotice / onError report UPWARD: a successful upload flips the Outcome to
// "has evidence", which unmounts this component — state kept here would take
// the filename-mismatch nudge (WU-A' §4) with it, and that nudge only exists
// on the success path (QA finding 2).
export default function EvidenceDropTarget({ agreementId, outcome, allOutcomes, onUploaded, onNotice, onError, onReceipt }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);

  const deliverableId = (outcome.deliverables || [])[0]?.id || null;

  const send = async (list) => {
    if (!list || list.length === 0) return;
    setBusy(true);
    if (onNotice) onNotice([]);
    if (onError) onError([]);
    if (onReceipt) onReceipt(null);
    const notices = [];
    const failures = [];
    const added = [];
    for (const file of list) {
      try {
        await evidenceFilesApi.upload(agreementId, file, deliverableId);
        added.push(file.name);
        const n = filenameMismatchNotice(file.name, outcome, allOutcomes);
        if (n) notices.push(n);
      } catch (err) {
        // Every failed file reports itself — one message for three files
        // would hide two of them (QA round 1, finding 5) — in plain words,
        // never the route's raw MIME-type list (QA round 2, F4).
        failures.push(uploadErrorMessage(file.name, err));
      }
    }
    if (onNotice) onNotice(notices);
    if (onError) onError(failures);
    if (onReceipt) onReceipt(receiptFor(added, list.length, outcome.name));
    setBusy(false);
    if (onUploaded) await onUploaded();
  };

  return (
    <div className="mt-3" data-testid="evidence-drop-target">
      <div
        className={`flex items-center gap-4 rounded-xl border-[1.5px] border-dashed px-4 py-3.5 cursor-pointer transition-colors ${
          over ? 'border-primary-400 bg-primary-50' : 'border-gray-400 bg-gray-50 hover:border-primary-300'
        } ${busy ? 'opacity-70 pointer-events-none' : ''}`}
        onClick={() => inputRef.current && inputRef.current.click()}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); send([...(e.dataTransfer.files || [])]); }}
      >
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-gray-900 truncate" title={dropTitleFor(outcome)}>{dropTitleFor(outcome)}</div>
          <div className="text-[11.5px] text-gray-600 mt-0.5">{dropSubtitleFor(outcome)}</div>
        </div>
        <button
          type="button"
          disabled={busy}
          className="text-[12.5px] font-semibold px-3.5 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-70 whitespace-nowrap"
          data-testid="add-evidence"
        >
          {busy ? 'Adding…' : 'Add evidence'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          className="hidden"
          onChange={(e) => { send([...(e.target.files || [])]); e.target.value = ''; }}
        />
      </div>
    </div>
  );
}
